package org.zq.astrock_ai.api.dto

import kotlinx.serialization.Serializable
import org.zq.astrock_ai.quant.QuantConsensus
import org.zq.astrock_ai.quant.StrategyInfo

@Serializable
data class AgentChatRequestDto(
    val stockCode: String,
    val stockName: String = "",
    val message: String,
    val sessionId: String? = null,
    /** 可选：客户端已知成本，写入演示持仓 */
    val costPrice: Double? = null,
    val quantity: Int? = null,
    /** 可选：客户端整理的当前研究台完整数据简报，优先喂给大模型 */
    val researchBrief: String? = null,
    /** 可选：当前自选代码，供 scan_quant_codes 使用 */
    val watchlistCodes: List<String> = emptyList(),
)

@Serializable
data class AgentChatResponseDto(
    val sessionId: String,
    val replyText: String,
    val structured: StructuredAnalysisDto? = null,
    /** 本地量化引擎：信号 + 回测摘要（由工具计算，非模型编造） */
    val quant: QuantResultDto? = null,
    /** 自选批量扫描结果（若调用了 scan_quant_codes） */
    val scan: QuantScanResponseDto? = null,
    val toolsCalled: List<String> = emptyList(),
    val dataTime: String? = null,
    val mode: String = "demo", // demo | kimi
    val error: String? = null,
)

@Serializable
data class QuantResultDto(
    val strategyId: String,
    val strategyName: String,
    val signal: String,
    val signalReason: String,
    val asOf: String,
    val price: Double = 0.0,
    val planSummary: String? = null,
    val entryHint: String? = null,
    val exitHint: String? = null,
    val invalidation: String? = null,
    val backtest: QuantBacktestDto? = null,
    val positionNote: String? = null,
    val consensus: QuantConsensus? = null,
)

@Serializable
data class QuantBacktestTradeDto(
    val entryDate: String,
    val exitDate: String,
    val entryPrice: Double,
    val exitPrice: Double,
    val returnPct: Double,
)

@Serializable
data class QuantBacktestDto(
    val lookbackDays: Int,
    val trades: Int,
    val winRate: Double,
    val totalReturnPct: Double,
    val maxDrawdownPct: Double,
    val note: String = "",
    /** 最近成交明细（用于列表与 K 线标记） */
    val tradeDetails: List<QuantBacktestTradeDto> = emptyList(),
    /** 权益曲线（起点≈1.0） */
    val equityCurve: List<Double> = emptyList(),
)

/** GET /api/stock/{code}/quant */
@Serializable
data class QuantPlanResponseDto(
    val code: String,
    val source: String,
    val quant: QuantResultDto,
    val strategies: List<StrategyInfo> = emptyList(),
)

/** GET /api/quant/scan?codes= */
@Serializable
data class QuantScanItemDto(
    val code: String,
    val name: String = "",
    val signal: String,
    val strategyId: String,
    val reason: String,
    val price: Double = 0.0,
    val asOf: String = "",
    val buyVotes: Int = 0,
    val sellVotes: Int = 0,
    val source: String = "demo",
    /** 建议卖出区间下限（近端压力带） */
    val sellLow: Double = 0.0,
    /** 建议卖出区间上限 */
    val sellHigh: Double = 0.0,
    /** 卖区方法说明，如「多锚点汇合压力带」 */
    val sellMethod: String = "",
    /** 卖区置信 0~1 */
    val sellConfidence: Double = 0.0,
    /** 建议卖出理由（完整条目） */
    val sellReasons: List<String> = emptyList(),
    /** 建议买入区间下限 */
    val buyLow: Double = 0.0,
    /** 建议买入区间上限 */
    val buyHigh: Double = 0.0,
    val buyMethod: String = "",
    val buyConfidence: Double = 0.0,
    val buyReasons: List<String> = emptyList(),
)

@Serializable
data class QuantScanResponseDto(
    val strategyId: String,
    val count: Int,
    val buyCount: Int = 0,
    val sellCount: Int = 0,
    val holdCount: Int = 0,
    val items: List<QuantScanItemDto> = emptyList(),
)

/**
 * 当前分析市场内的涨停/接近涨停一行（附在创业板或上证结果里，不是新市场）。
 * 行情参考，非隔夜荐股入选名单。
 */
@Serializable
data class LimitUpStockDto(
    val code: String = "",
    val name: String = "",
    /** cyb / sh */
    val board: String = "",
    val price: Double = 0.0,
    val changePct: Double = 0.0,
    /** sealed=已涨停；near=接近涨停（涨停机会） */
    val kind: String = "sealed",
    /** 距涨停还差几个百分点（已封约为 0） */
    val toLimitPct: Double = 0.0,
    /** 首次触及涨停 HH:mm；接近涨停可为空 */
    val firstLimitTime: String = "",
    /** 已封：开盘后约多久；接近：如「距涨停约1.2%」 */
    val sealAfterOpen: String = "",
    /** 交易分钟数（不含午休）；未知为 -1 */
    val sealMinutes: Int = -1,
)

/** GET /api/quant/recommend — 程序漏斗筛选后的候选（最多 5） */
@Serializable
data class RecommendStockItemDto(
    val code: String,
    val name: String = "",
    val board: String = "",
    val score: Double = 0.0,
    val confidence: Double = 0.0,
    val signal: String = "HOLD",
    val price: Double = 0.0,
    val changePct: Double = 0.0,
    val turnover: Double = 0.0,
    val technicalScore: Double = 0.0,
    val liquidityScore: Double = 0.0,
    val dayScore: Double = 0.0,
    val riskScore: Double = 0.0,
    val reasons: List<String> = emptyList(),
    val risks: List<String> = emptyList(),
    val asOf: String = "",
    val source: String = "eastmoney",
    /** 建议卖出区间下限 */
    val sellLow: Double = 0.0,
    /** 建议卖出区间上限 */
    val sellHigh: Double = 0.0,
    /** 卖出区间说明 */
    val sellHint: String = "",
    val sellMethod: String = "",
    val sellConfidence: Double = 0.0,
    val sellReasons: List<String> = emptyList(),
    /** 卖出多方视角摘要（结构/量能/均线/波动/翻红/回测等） */
    val sellPerspectives: List<String> = emptyList(),
    val buyLow: Double = 0.0,
    val buyHigh: Double = 0.0,
    val buyHint: String = "",
    val buyMethod: String = "",
    val buyConfidence: Double = 0.0,
    val buyReasons: List<String> = emptyList(),
    /** 程序结构化综合分析（非大模型改写分数） */
    val analysis: String = "",
    /** 观望且具备买区，优先等待回踩而非追涨 */
    val preferWaitBuy: Boolean = false,
    /** 适合隔夜短线（今买明日卖；卖出可早盘或尾盘） */
    val overnightFit: Boolean = false,
    /** 适合中线 3–6 个月 */
    val swingFit: Boolean = false,
    /** 次日上涨路径形态（并入隔夜短线，非独立风格；不问今日涨停/跌停） */
    val limitHuntFit: Boolean = false,
    /** 深跌反抽：盘中曾明显深跌后大幅回升 */
    val deepRebound: Boolean = false,
    /** 早盘深跌后回升、冲翻红机会 */
    val morningDipRebound: Boolean = false,
    /** 缩量止跌形态（近弱后缩量收稳） */
    val volumeDryStop: Boolean = false,
    /** 板块龙头启动（板块/龙头转暖跟随） */
    val sectorLeaderStart: Boolean = false,
    /** 低吸反抽次日博弈 */
    val lowAbsorbRebound: Boolean = false,
    /** 自日低回升的百分点 */
    val reboundPts: Double = 0.0,
    /** 分时日最低相对昨收涨跌幅 % */
    val dayLowChangePct: Double = 0.0,
    /** 早盘最低相对昨收涨跌幅 % */
    val morningLowChangePct: Double = 0.0,
    /** 隔夜出场提示（次日早盘或尾盘清仓） */
    val overnightExitHint: String = "",
    /** 现价相对建议卖出价的上行空间 %（≥3 才入选） */
    val sellUpsidePct: Double = 0.0,
    /** 用于对比的卖出参考价（优先卖区下限） */
    val sellTargetPrice: Double = 0.0,
    /** 当日涨幅偏高，追高风险 */
    val chaseRisk: Boolean = false,
    /** K 线 asOf 偏旧，建议刷新后再决策 */
    val klineStale: Boolean = false,
    /** 成交额（元）已有 turnover；此处为成交量（手，约） */
    val volume: Double = 0.0,
    /** 换手率 % */
    val turnoverRate: Double = 0.0,
    /** 量比（优先行情量比，其次近20日均量比） */
    val volumeRatio: Double = 0.0,
    /** 总市值（元） */
    val marketCap: Double = 0.0,
    /** 流通市值（元） */
    val floatCap: Double = 0.0,
    /** K线位置：是否高点等 */
    val klinePosition: String = "",
    val nearHigh: Boolean = false,
    /** 最新新闻标题 */
    val latestNews: List<String> = emptyList(),
    /** 最新公司公告标题 */
    val latestAnnouncements: List<String> = emptyList(),
    val newsScore: Double = 0.0,
    /** 资讯事件标签：重组催化/回购/立案等 */
    val newsEventTags: List<String> = emptyList(),
    /** 重组信号摘要（空=无重组相关标题） */
    val reorgLabel: String = "",
    /** K线结构：回踩/突破等 */
    val structureLabel: String = "",
    val structureScore: Double = 0.0,
    /** 所属行业与强度 */
    val industry: String = "",
    val industryTrend: String = "",
    val industryRank: Int = 0,
    /** 板块龙头联动 */
    val sectorLeaderCode: String = "",
    val sectorLeaderName: String = "",
    val sectorLeaderChangePct: Double = 0.0,
    val sectorBoardChangePct: Double = 0.0,
    val sectorLeaderLabel: String = "",
    /** 分时结构标签 */
    val intradayLabel: String = "",
    val intradayScore: Double = 0.0,
    /** 近几日是否上龙虎榜 */
    val onLhb: Boolean = false,
    val lhbNetAmt: Double = 0.0,
    val lhbDate: String = "",
    val lhbReason: String = "",
    /** 财务/估值简报 */
    val financeReport: String = "",
    val pe: Double = 0.0,
    val pb: Double = 0.0,
    val eps: Double = 0.0,
    val roe: Double = 0.0,
    val revenueYoY: Double = 0.0,
    val profitYoY: Double = 0.0,
    val financeScore: Double = 0.0,
    /** 今日主力净流入（元） */
    val todayMainNet: Double = 0.0,
    /** 近几日主力净流入合计（元） */
    val recentMainNet: Double = 0.0,
    /** 资金面短标签，如「今日主力净流入偏强」 */
    val fundLabel: String = "",
    /** 当天盘中十因子检查清单 */
    val sessionChecklist: List<String> = emptyList(),
    /** 盘中因子综合分 */
    val sessionScore: Double = 0.0,
    /** 二～五类背景检查清单（近月/公告/基本面/规则） */
    val backgroundChecklist: List<String> = emptyList(),
    /** 背景因子综合分 */
    val backgroundScore: Double = 0.0,
    /** 隔夜操作指引（次日早盘/尾盘兑现等） */
    val overnightHints: List<String> = emptyList(),
    /** 近约1个月走势标签 */
    val monthTrendLabel: String = "",
    /** 近月累计涨跌幅 % */
    val monthReturnPct: Double = 0.0,
    /** 近5日涨跌幅 % */
    val recent5ReturnPct: Double = 0.0,
    /** 近月走势分 */
    val monthTrendScore: Double = 0.0,
    /** 近约半年走势标签 */
    val halfYearLabel: String = "",
    /** 近半年累计涨跌幅 % */
    val halfReturnPct: Double = 0.0,
    /** 昨日涨跌幅 %（跨日高潮判断） */
    val yesterdayReturnPct: Double = 0.0,
    /** 风格/拥挤检查清单 */
    val styleChecklist: List<String> = emptyList(),
    val holdPeriodLabel: String = "",
    val ret3mPct: Double = 0.0,
    val ret6mPct: Double = 0.0,
    /** A股涨跌经验：震荡/量价/线上阴线等 */
    val tapeLabel: String = "",
    val tapeChecklist: List<String> = emptyList(),
    val seasonLabel: String = "",
    val windowLabel: String = "",
    val windowChecklist: List<String> = emptyList(),
)

@Serializable
data class RecommendResponseDto(
    val generatedAt: String = "",
    val marketStatus: String = "UNKNOWN",
    val marketRegime: String = "",
    val riskLevel: String = "",
    val recommendedPositionLimit: Double = 0.0,
    val preferredStyles: List<String> = emptyList(),
    val avoidStyles: List<String> = emptyList(),
    val candidatePoolSize: Int = 0,
    val scoredCount: Int = 0,
    val dataComplete: Boolean = true,
    val staleSources: List<String> = emptyList(),
    val note: String = "程序量化筛选结果；大模型仅负责解释，不得改写分数。研究工具非投资建议。",
    /** 本次筛选市场：cyb=创业板，sh=上证主板（单选） */
    val boards: List<String> = emptyList(),
    val boardsLabel: String = "",
    /** 操作提示（隔夜短线、仓位等） */
    val playbook: List<String> = emptyList(),
    /** 交易风格：overnight=今买明日卖（早盘或尾盘均可出场）；swing=中线3–6个月 */
    val tradeStyle: String = "overnight",
    /** 北向（沪+深股通）当日净流入文案 */
    val northboundFlow: String = "",
    /** 北向净流入（亿元） */
    val northboundNetYi: Double = 0.0,
    /**
     * 是否强制实时刷新后打分：每次请求清空缓存并逐票拉最新报价/分时/资金/公告。
     */
    val liveRefresh: Boolean = false,
    /**
     * 候选池来源：eastmoney_full / eastmoney_sample / cache / tencent_seed 等。
     * 任意时刻都应能拉到数据；cache/seed 表示东财瞬时不可用时的降级。
     */
    val candidateSource: String = "",
    /** 若来自本地缓存，缓存写入时间 */
    val candidateCacheAsOf: String = "",
    /**
     * 空仓时的主要淘汰原因汇总，如「近月走势不过 320只」「上行空间不足 180只」。
     * 有入选时也可附带漏斗统计（可选）。
     */
    val emptyReasons: List<String> = emptyList(),
    /** 未最终入选但分数较高的「差一点」标的（供复盘，非推荐） */
    val nearMisses: List<RecommendPreviewItemDto> = emptyList(),
    /** 隔夜外盘：美股板块/商品/国际新闻（荐股时现场拉取） */
    val globalOvernight: GlobalOvernightDto = GlobalOvernightDto(),
    val stocks: List<RecommendStockItemDto> = emptyList(),
    /**
     * 当前所选市场（创业板或上证）内的涨停/接近涨停机会。
     * 不是新增市场入口，仅附在本次分析结果里。
     */
    val limitUps: List<LimitUpStockDto> = emptyList(),
    /** 涨停机会快照说明，如「创业板 · 2026-08-20」 */
    val limitUpAsOf: String = "",
    /** 服务端自动复盘调参摘要（样本/命中率/门槛微调） */
    val autoTuneNote: String = "",
)

@Serializable
data class GlobalQuoteDto(
    val name: String = "",
    val changePct: Double = 0.0,
    /** index / sector / commodity / stock */
    val group: String = "",
)

@Serializable
data class GlobalHeadlineDto(
    val title: String = "",
    val source: String = "",
)

@Serializable
data class GlobalOvernightDto(
    val asOf: String = "",
    val quotes: List<GlobalQuoteDto> = emptyList(),
    val headlines: List<GlobalHeadlineDto> = emptyList(),
    /** 市场级摘要：美股热门→A股 + 国内外新闻 */
    val mappingNotes: List<String> = emptyList(),
    /** 美股热门/承压板块及对应 A 股关注方向 */
    val hotSectors: List<GlobalHotSectorDto> = emptyList(),
)

@Serializable
data class GlobalHotSectorDto(
    val name: String = "",
    val changePct: Double = 0.0,
    val theme: String = "",
    val aShareHints: List<String> = emptyList(),
    val hot: Boolean = true,
)

/** 异步荐股任务启动回执 */
@Serializable
data class RecommendJobStartDto(
    val jobId: String = "",
)

/**
 * 细算过程中的临时预览行（随进度不断替换，最终以 [RecommendProgressDto.result] 为准）。
 */
@Serializable
data class RecommendPreviewItemDto(
    val code: String = "",
    val name: String = "",
    val score: Double = 0.0,
    val changePct: Double = 0.0,
    val overnightFit: Boolean = false,
    val note: String = "",
)

/**
 * 荐股实时进度（轮询 `/api/quant/recommend/status`）。
 * [done]=true 时 [result] 为最终名单（可为空仓）。
 */
@Serializable
data class RecommendProgressDto(
    val jobId: String = "",
    /** 当前大步骤，如「拉取候选池」「细算打分」 */
    val stage: String = "",
    /** 细节，如「300001 平安银行 · 120/800」 */
    val detail: String = "",
    /** 0～100 */
    val percent: Int = 0,
    val done: Boolean = false,
    val error: String = "",
    /** 已走过的步骤标题（便于 UI 勾选展示） */
    val steps: List<String> = emptyList(),
    /** 分析动态流水（不断追加/滚动，供空白区实时刷新） */
    val log: List<String> = emptyList(),
    /** 当前临时入围预览（随细算更新替换，非最终名单） */
    val preview: List<RecommendPreviewItemDto> = emptyList(),
    val result: RecommendResponseDto? = null,
)

@Serializable
data class StructuredAnalysisDto(
    val conclusion: ConclusionDto,
    val position: PositionViewDto,
    val trend: TrendViewDto,
    val buyPlan: BuyPlanDto,
    val sellPlan: SellPlanDto,
    val risk: RiskDto,
    val evidence: List<String> = emptyList(),
    val dataTime: String,
)

@Serializable
data class ConclusionDto(
    val action: String,
    val title: String,
    val summary: String,
)

@Serializable
data class PositionViewDto(
    val level: String,
    val description: String,
)

@Serializable
data class TrendViewDto(
    val shortTerm: String,
    val mediumTerm: String,
    val confidence: Double,
)

@Serializable
data class BuyPlanDto(
    val enabled: Boolean,
    val zones: List<PriceZoneDto> = emptyList(),
)

@Serializable
data class SellPlanDto(
    val zones: List<PriceZoneDto> = emptyList(),
)

@Serializable
data class PriceZoneDto(
    val low: Double,
    val high: Double,
    val condition: String,
)

@Serializable
data class RiskDto(
    val invalidationPrice: Double,
    val warnings: List<String> = emptyList(),
)

@Serializable
data class AgentHealthDto(
    val kimiConfigured: Boolean,
    val model: String,
    val tools: List<String>,
)

/** 单票自动复盘一行（荐股日 → 结算日） */
@Serializable
data class RecommendReviewItemDto(
    val board: String = "",
    val style: String = "",
    val tradeDate: String = "",
    val settleDate: String = "",
    val code: String = "",
    val name: String = "",
    /** picked=入选；near_miss=差一点 */
    val kind: String = "picked",
    val pathTag: String = "",
    val entryPrice: Double = 0.0,
    val buyLow: Double = 0.0,
    val buyHigh: Double = 0.0,
    val sellLow: Double = 0.0,
    val sellHigh: Double = 0.0,
    val nextOpen: Double = 0.0,
    val nextClose: Double = 0.0,
    val nextHigh: Double = 0.0,
    val nextLow: Double = 0.0,
    /** 结算日收盘相对荐股价涨跌 % */
    val nextRetPct: Double = 0.0,
    /** 结算日开盘相对荐股价涨跌 % */
    val openRetPct: Double = 0.0,
    /** 结算日收盘相对昨收（荐股日收盘）涨跌 % —— 对准「收红」 */
    val dayRetPct: Double = 0.0,
    val hitUp: Boolean = false,
    /** 相对昨收收红（dayRetPct > 0） */
    val hitRed: Boolean = false,
    val hitLimit: Boolean = false,
    val hitSellZone: Boolean = false,
    val weakOpen: Boolean = false,
    val label: String = "",
    val settledAtMs: Long = 0L,
)

@Serializable
data class RecommendReviewSummaryDto(
    val boards: String = "",
    val style: String = "",
    val days: Int = 30,
    val sampleCount: Int = 0,
    val hitUpCount: Int = 0,
    val hitUpRate: Double = 0.0,
    /** 相对昨收收红样本数/率 */
    val hitRedCount: Int = 0,
    val hitRedRate: Double = 0.0,
    val avgNextRetPct: Double = 0.0,
    val hitLimitCount: Int = 0,
    val hitSellZoneCount: Int = 0,
    val weakOpenCount: Int = 0,
    val labelCounts: Map<String, Int> = emptyMap(),
    val note: String = "服务端自动复盘：隔夜按次一交易日收盘结算；hitRed=相对昨收收红；hitUp=相对入场价微涨。中线按第5个交易日。研究工具非投资建议。",
)

@Serializable
data class RecommendReviewListDto(
    val summary: RecommendReviewSummaryDto = RecommendReviewSummaryDto(),
    val items: List<RecommendReviewItemDto> = emptyList(),
)

@Serializable
data class RecommendReviewRunDto(
    val ok: Boolean = false,
    val settledRows: Int = 0,
    val scannedArchives: Int = 0,
    val message: String = "",
    /** 复盘后自动优化摘要（若本轮触发） */
    val optimizeNote: String = "",
)

/** 服务端自动复盘驱动的漏斗调参（按 board+style） */
@Serializable
data class RecommendTuneParamsDto(
    val board: String = "",
    val style: String = "",
    /** 叠加到隔夜/中线入选门槛（基准 58） */
    val scoreThresholdDelta: Double = 0.0,
    /** 叠加到次日上涨门槛（基准 50） */
    val scoreLimitHuntDelta: Double = 0.0,
    val preLimitBoostMul: Double = 1.0,
    val limitHuntScoreMul: Double = 1.0,
    val volBoostMul: Double = 1.0,
    val overnightBoostMul: Double = 1.0,
    val chasePenaltyMul: Double = 1.0,
    /** 叠加到最低上行空间 % */
    val minUpsideDelta: Double = 0.0,
    val sampleCount: Int = 0,
    val hitUpRate: Double = 0.0,
    /** 相对昨收收红率 %（隔夜优化主目标） */
    val hitRedRate: Double = 0.0,
    val avgNextRetPct: Double = 0.0,
    val note: String = "",
    val updatedAtMs: Long = 0L,
    val version: Int = 0,
)

@Serializable
data class RecommendTuneListDto(
    val items: List<RecommendTuneParamsDto> = emptyList(),
    val note: String = "由盘后自动复盘驱动；样本不足不调参；乘数限制在 0.75～1.30，门槛±4。",
)

@Serializable
data class RecommendOptimizeRunDto(
    val ok: Boolean = false,
    val updated: Int = 0,
    val items: List<RecommendTuneParamsDto> = emptyList(),
    val message: String = "",
)

/** 单日涨幅≥阈值标的的前日画像分析摘要 */
@Serializable
data class GainerPriorInsightDto(
    val tradeDate: String = "",
    val minChangePct: Double = 5.0,
    val gainerCount: Int = 0,
    val analyzedCount: Int = 0,
    val avgChangePct: Double = 0.0,
    /** 前日贴 MA5 且不下行占比 % */
    val priorMa5ClassicPct: Double = 0.0,
    /** 前日缩量止跌占比 % */
    val priorDryVolPct: Double = 0.0,
    /** 前日缩量/温和量占比 % */
    val priorMildVolPct: Double = 0.0,
    /** 前日高潮盘占比 %（近高/巨量等） */
    val priorClimaxPct: Double = 0.0,
    /** 前日近似「涨停前一日」画像占比 % */
    val priorPreLimitPct: Double = 0.0,
    /** 前日涨幅均值 % */
    val avgPriorDayRetPct: Double = 0.0,
    val boardCounts: Map<String, Int> = emptyMap(),
    val samples: List<GainerPriorSampleDto> = emptyList(),
    val note: String = "",
    val optimizeNote: String = "",
)

@Serializable
data class GainerPriorSampleDto(
    val code: String = "",
    val name: String = "",
    val board: String = "",
    val changePct: Double = 0.0,
    val priorDate: String = "",
    val priorDayRetPct: Double = 0.0,
    val priorMa5Classic: Boolean = false,
    val priorDryVol: Boolean = false,
    val priorMildVol: Boolean = false,
    val priorClimax: Boolean = false,
    val priorPreLimit: Boolean = false,
    val priorMa5DistPct: Double = 0.0,
    val priorVolRatio: Double = 0.0,
)

@Serializable
data class GainerAnalyzeRunDto(
    val ok: Boolean = false,
    val insight: GainerPriorInsightDto = GainerPriorInsightDto(),
    val message: String = "",
)

@Serializable
data class RecommendPickBriefDto(
    val code: String = "",
    val name: String = "",
    val score: Double = 0.0,
    val changePct: Double = 0.0,
    val overnightFit: Boolean = false,
    val swingFit: Boolean = false,
    val reasons: List<String> = emptyList(),
)

@Serializable
data class RecommendIntelligenceDto(
    val boards: String = "",
    val style: String = "",
    val generatedAt: String = "",
    val marketRegime: String = "",
    val riskLevel: String = "",
    val recommendedPositionLimit: Double = 0.0,
    /** 一句市场结论：强弱/拥挤 + 入选或空仓 */
    val marketConclusion: String = "",
    val picks: List<RecommendPickBriefDto> = emptyList(),
    val emptyReasons: List<String> = emptyList(),
    val nearMisses: List<RecommendPreviewItemDto> = emptyList(),
    val review: RecommendReviewSummaryDto = RecommendReviewSummaryDto(),
    val yesterdayDate: String = "",
    val yesterday: List<RecommendReviewItemDto> = emptyList(),
    val tune: RecommendTuneParamsDto = RecommendTuneParamsDto(),
    val gainer: GainerPriorInsightDto = GainerPriorInsightDto(),
    val gainerPrev: GainerPriorInsightDto = GainerPriorInsightDto(),
    val todayRedDate: String = "",
    val todayRed: List<RecommendReviewItemDto> = emptyList(),
    val gainerSessions: List<GainerPriorInsightDto> = emptyList(),
    val note: String = "程序漏斗拍板；大模型只解释；不得改分或新增未入选代码。研究工具非投资建议。",
)
